import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Minus } from "@/components/icons";
import logo from "@/assets/thevibingco_logo.svg.asset.json";
import cursorLogo from "@/assets/logos/cursor.svg.asset.json";
import claudeLogo from "@/assets/logos/claude.svg.asset.json";
import openaiLogo from "@/assets/logos/openai.svg.asset.json";
import lovableLogo from "@/assets/logos/lovable.svg.asset.json";
import { MagneticButton } from "@/components/magnetic-button";
import { RelatedGuides } from "@/components/related-guides";
import { SiteFooter } from "@/components/site-footer";

const TOOL_LOGOS: Record<string, { src: string; tilt: number }> = {
  Lovable: { src: lovableLogo.url, tilt: -2.4 },
  Cursor: { src: cursorLogo.url, tilt: 1.8 },
  "Claude Code": { src: claudeLogo.url, tilt: -1.4 },
  Codex: { src: openaiLogo.url, tilt: 2.6 },
};

function ToolLogo({ name, size = 18 }: { name: string; size?: number }) {
  const t = TOOL_LOGOS[name];
  if (!t) return null;
  return (
    <span
      className="inline-grid place-items-center rounded-full bg-white ring-1 ring-black/[0.06] shadow-[0_4px_14px_-6px_rgba(20,20,40,0.18)] overflow-hidden shrink-0"
      style={{ height: size, width: size }}
      aria-hidden
    >
      <img src={t.src} alt="" className="object-contain" style={{ height: size * 0.62, width: size * 0.62 }} />
    </span>
  );
}

function ToolPill({ name }: { name: string }) {
  const t = TOOL_LOGOS[name];
  if (!t) return <>{name}</>;
  return (
    <span
      style={{ transform: `rotate(${t.tilt}deg)`, transformOrigin: "50% 60%" }}
      className="inline-flex items-center gap-1.5 align-middle mx-[0.05em] -translate-y-[0.06em] rounded-full bg-white pl-1.5 pr-3 py-[0.14em] ring-1 ring-black/[0.06] shadow-[0_6px_18px_-8px_rgba(20,20,40,0.22)] text-[0.62em] tracking-[-0.005em] font-display text-foreground/90 leading-none whitespace-nowrap"
    >
      <span className="grid place-items-center h-[1.5em] w-[1.5em] rounded-full bg-foreground/[0.04] overflow-hidden">
        <img src={t.src} alt="" aria-hidden className="h-[0.95em] w-[0.95em] object-contain" />
      </span>
      {name}
    </span>
  );
}

export const FAQS = [
  {
    q: "Lovable vs Cursor — which one should I start with?",
    a: "If you're going from idea to a working app with a database, auth and a public URL, start with Lovable. If you already have a codebase and want an AI pair-programmer that lives in your editor, start with Cursor. Most serious builders use Lovable to spin up the first 80% and Cursor for the long tail.",
  },
  {
    q: "Is Claude Code better than Cursor?",
    a: "Different shape. Cursor is an IDE — you watch every change. Claude Code is a terminal agent — you give it a task and walk away. Claude Code tends to win on multi-file refactors and long autonomous runs; Cursor wins on tight feedback loops and visible diffs while you work.",
  },
  {
    q: "Where does Codex fit in?",
    a: "Codex is OpenAI's coding agent. It runs in a CLI, an IDE extension, on the web and in the ChatGPT app, and uses GPT-5.5 / GPT-5.4-Codex models. It overlaps heavily with Claude Code; the choice usually comes down to whether your team is already on ChatGPT Plus/Pro or Anthropic Pro/Max.",
  },
  {
    q: "Can I use Lovable with Cursor, Claude Code, or Codex?",
    a: "Yes — that's the most common professional setup. Build the product in Lovable, then open the same repo (via the GitHub integration) in Cursor, Claude Code, or Codex when you need surgical changes, complex refactors, or work the AI builder is struggling with.",
  },
  {
    q: "Which one is cheapest?",
    a: "Lovable Free and Cursor Hobby are both $0. Past free, the cheapest paid tier is Lovable Pro at $25/month. Cursor Pro is $20/month, Codex via ChatGPT Plus is $20/month, and Claude Code via Claude Pro is $20/month — but the agentic CLIs burn through tokens fast, so heavy users typically end up on $100–$200/month tiers.",
  },
  {
    q: "Do these tools host the app for me?",
    a: "Only Lovable. Cursor, Claude Code and Codex generate code — you still need to deploy it somewhere (Vercel, Cloudflare, Fly, your own server). Lovable ships with hosting, a database, auth, storage and edge functions out of the box.",
  },
];

export const Route = createFileRoute("/guides/lovable-vs-cursor")({
  head: () => ({
    meta: [
      { title: "Lovable vs Cursor vs Claude Code vs Codex (2026)" },
      {
        name: "description",
        content:
          "Honest 2026 comparison of Lovable, Cursor, Claude Code and Codex — features, pricing, models, hosting, backend, and which AI coding tool to pick.",
      },
      { property: "og:title", content: "Lovable vs Cursor vs Claude Code vs Codex (2026)" },
      ...ogImageMeta("guides-lovable-vs-cursor", "Share card: Lovable vs Cursor vs Claude Code vs Codex"),
      {
        property: "og:description",
        content:
          "Side-by-side comparison of the four AI coding tools that matter in 2026 — checkmark tables, real pricing, and a clear pick for each kind of project.",
      },
      { property: "og:url", content: "https://vibing.inc/guides/lovable-vs-cursor" },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/guides/lovable-vs-cursor" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Lovable vs Cursor vs Claude Code vs Codex (2026)",
          description:
            "Side-by-side comparison of Lovable, Cursor, Claude Code, and Codex — features, models, pricing, hosting, and how to pick.",
          author: { "@type": "Organization", name: "the Vibing Company" },
          publisher: { "@type": "Organization", name: "the Vibing Company" },
          datePublished: "2026-06-11",
          dateModified: "2026-06-11",
          mainEntityOfPage: "https://vibing.inc/guides/lovable-vs-cursor",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Guide,
});

function Guide() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
      <Hero />
      <Verdict />
      <FeatureMatrix />
      <PricingTable />
      <Profiles />
      <RestOfField />
      <Decision />
      <FAQ />
      <CTA />
      <RelatedGuides current="/guides/lovable-vs-cursor" service={{ to: "/services/lovable-development", label: "Lovable development" }} />
      </main>
      <SiteFooter />
    </div>
  );
}


function Hero() {
  return (
    <section aria-labelledby="g-h" className="relative mx-auto max-w-[1100px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-12">
      <h1 id="g-h" className="mt-8 font-display tracking-[-0.035em] leading-[1.02]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
        <ToolPill name="Lovable" /> vs <ToolPill name="Cursor" /> vs <ToolPill name="Claude Code" /> vs <ToolPill name="Codex" />.
      </h1>
      <p className="mt-10 max-w-[62ch] text-xl md:text-2xl leading-snug text-foreground">
        The four AI coding tools that actually matter in 2026. We build with all of them every week — here is the honest, up-to-date side-by-side, with pricing, models, and a clear pick for each kind of project.
      </p>
      <p className="mt-6 max-w-[62ch] text-sm text-muted-foreground">
        Updated June 2026 · Reflects Cursor 3.5, Claude Code with Sonnet 4.5, Codex on GPT-5.5 / GPT-5.4-Codex, and Lovable Cloud.
      </p>
    </section>
  );
}

function Verdict() {
  const picks = [
    { tool: "Lovable", line: "Idea → live full-stack app with a database, auth, hosting and a URL — fastest path from prompt to product." },
    { tool: "Cursor", line: "Pair-programming inside your existing codebase. You watch every diff in a VS Code-style IDE." },
    { tool: "Claude Code", line: "Long-running autonomous agent in the terminal. Hand off a task, walk away, review the PR." },
    { tool: "Codex", line: "OpenAI's coding agent across CLI, IDE, web and iOS. Strong if your team already lives in ChatGPT." },
  ];
  return (
    <section aria-labelledby="v-h" className="relative border-t border-border/60 mx-auto max-w-[1100px] px-6 md:px-10 py-16 md:py-24">
      <h2 id="v-h" className="fluid-h2 font-display max-w-[18ch]">The short answer.</h2>
      <ul className="mt-10 grid md:grid-cols-2 gap-5">
        {picks.map((p) => (
          <li key={p.tool} className="rounded-3xl border border-border bg-background p-7 md:p-9">
            <h3 className="font-display tracking-[-0.03em]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2rem)" }}>{p.tool}</h3>
            <p className="mt-4 text-base md:text-lg text-muted-foreground leading-snug">{p.line}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

type Cell = boolean | "partial" | string;
type Row = { label: string; cells: [Cell, Cell, Cell, Cell] };

const TOOLS = ["Lovable", "Cursor", "Claude Code", "Codex"] as const;

const FEATURE_ROWS: { group: string; rows: Row[] }[] = [
  {
    group: "Where it runs",
    rows: [
      { label: "Browser — no install", cells: [true, false, false, true] },
      { label: "Desktop IDE", cells: [false, true, "partial", "partial"] },
      { label: "Terminal / CLI", cells: [false, false, true, true] },
      { label: "Mobile app", cells: [true, false, "partial", true] },
      { label: "Slack / chat integration", cells: [false, false, true, true] },
    ],
  },
  {
    group: "What you get out of the box",
    rows: [
      { label: "Generates frontend code", cells: [true, true, true, true] },
      { label: "Builds full apps from a prompt", cells: [true, "partial", "partial", "partial"] },
      { label: "Hosted, shareable URL on save", cells: [true, false, false, false] },
      { label: "Managed database + auth + storage", cells: [true, false, false, false] },
      { label: "Edge functions / backend logic", cells: [true, false, false, false] },
      { label: "Built-in AI gateway (no API key)", cells: [true, false, false, false] },
      { label: "GitHub two-way sync", cells: [true, true, true, true] },
    ],
  },
  {
    group: "How it works with code",
    rows: [
      { label: "Multi-file edits / agent mode", cells: [true, true, true, true] },
      { label: "Cloud agents (parallel, async)", cells: [true, true, true, true] },
      { label: "Runs tests + iterates on failures", cells: ["partial", true, true, true] },
      { label: "Persistent project memory", cells: [true, true, true, true] },
      { label: "MCP servers / external tools", cells: [true, true, true, true] },
      { label: "Custom skills / hooks", cells: [true, true, true, true] },
      { label: "Code review on pull requests", cells: ["partial", true, true, true] },
    ],
  },
  {
    group: "Models you can use",
    rows: [
      { label: "Frontier models from multiple labs", cells: [true, true, false, false] },
      { label: "Anthropic Claude (Sonnet 4.5 / Opus)", cells: [true, true, true, false] },
      { label: "OpenAI GPT-5 / GPT-5.5 family", cells: [true, true, false, true] },
      { label: "Google Gemini 2.5 / 3", cells: [true, true, false, false] },
      { label: "Image generation built in", cells: [true, "partial", false, true] },
    ],
  },
  {
    group: "For teams & production",
    rows: [
      { label: "SSO (SAML / OIDC)", cells: [true, true, true, true] },
      { label: "Audit logs", cells: [true, true, true, true] },
      { label: "Role-based access control", cells: [true, true, true, true] },
      { label: "SOC 2 compliant", cells: [true, true, true, true] },
      { label: "Data not used to train models", cells: [true, true, true, true] },
    ],
  },
];

function CellMark({ value }: { value: Cell }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center justify-center size-8 rounded-full bg-electric text-background">
        <Check className="size-5" strokeWidth={3} aria-hidden />
        <span className="sr-only">Yes</span>
      </span>
    );
  }
  if (value === "partial") {
    return (
      <span className="inline-flex items-center justify-center size-8 rounded-full border border-border bg-secondary/40 text-muted-foreground">
        <Minus className="size-5" strokeWidth={3} aria-hidden />
        <span className="sr-only">Partial</span>
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center justify-center size-8 rounded-full border border-border bg-background text-muted-foreground/60">
        <span aria-hidden className="block size-1.5 rounded-full bg-muted-foreground/50" />
        <span className="sr-only">No</span>
      </span>
    );
  }
  return <span className="text-sm md:text-base text-foreground">{value}</span>;
}

function FeatureMatrix() {
  return (
    <section aria-labelledby="fm-h" className="relative border-t border-border/60 bg-secondary/40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
        <h2 id="fm-h" className="fluid-h2 font-display max-w-[22ch]">Feature by feature.</h2>
        <p className="mt-5 max-w-[60ch] text-muted-foreground">
          A solid dot is full support. A dash is partial — possible, but with caveats. An empty circle means not available out of the box.
        </p>

        <div className="mt-12 space-y-12">
          {FEATURE_ROWS.map((g) => (
            <div key={g.group} className="rounded-3xl border border-border bg-background overflow-hidden">
              <div className="px-6 md:px-8 py-5 border-b border-border bg-secondary/30">
                <h3 className="font-display tracking-[-0.02em] text-lg md:text-xl">{g.group}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left min-w-[640px]">
                  <thead>
                    <tr className="text-sm text-muted-foreground">
                      <th className="px-6 md:px-8 py-4 font-medium w-[36%]">Capability</th>
                      {TOOLS.map((t) => (
                        <th key={t} className="px-3 md:px-4 py-4 font-medium text-center">
                          <span className="inline-flex items-center gap-2 justify-center"><ToolLogo name={t} size={20} />{t}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => (
                      <tr key={r.label} className="border-t border-border/60">
                        <td className="px-6 md:px-8 py-4 text-sm md:text-base">{r.label}</td>
                        {r.cells.map((c, i) => (
                          <td key={i} className="px-3 md:px-4 py-4 text-center">
                            <div className="flex justify-center"><CellMark value={c} /></div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PRICING: { tier: string; cells: [string, string, string, string]; note?: string }[] = [
  { tier: "Free", cells: ["$0 — 5 daily credits, hosted apps, Cloud backend", "$0 — limited Agent + Tab completions", "Not offered (use Anthropic API pay-as-you-go)", "Limited trial via ChatGPT Free"] },
  { tier: "Entry paid", cells: ["Pro · $25/mo — 100 credits, custom domain, no badge", "Pro · $20/mo — frontier models, MCPs, cloud agents", "Claude Pro · $20/mo — Claude Code with Sonnet 4.5", "ChatGPT Plus · $20/mo — Codex CLI + IDE + web"] },
  { tier: "Power user", cells: ["Business · $50/mo — SSO, security center, team workspace", "Pro+ / Ultra — extended usage on the $20 plan tier", "Claude Max 5x · $100/mo · Max 20x · $200/mo", "ChatGPT Pro · from $100/mo — 5x or 20x Codex limits"] },
  { tier: "Teams", cells: ["Enterprise — pooled credits, contracts, scale", "Teams · $40/user/mo — Bugbot, shared context, SSO", "Claude Team / Enterprise — per-seat pricing", "ChatGPT Business / Enterprise — per-seat + admin"] },
  { tier: "What you actually pay for", cells: ["AI prompts + Cloud usage, metered in credits", "Agent requests + frontier model calls", "Anthropic API tokens (input + output)", "OpenAI API tokens or ChatGPT credits"] },
];

function PricingTable() {
  return (
    <section aria-labelledby="p-h" className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
      <h2 id="p-h" className="fluid-h2 font-display max-w-[20ch]">Pricing, side by side.</h2>
      <p className="mt-5 max-w-[60ch] text-muted-foreground">
        Public list prices in USD as of June 2026. Agentic tools (Cursor, Claude Code, Codex) are usage-metered on top of the subscription — heavy users routinely spend 3–5× the headline price.
      </p>
      <div className="mt-12 rounded-3xl border border-border bg-background overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[820px]">
            <thead>
              <tr className="text-sm text-muted-foreground bg-secondary/40 border-b border-border">
                <th className="px-6 md:px-8 py-5 font-medium w-[18%]">Tier</th>
                {TOOLS.map((t) => (
                  <th key={t} className="px-4 md:px-5 py-5 font-medium">
                    <span className="inline-flex items-center gap-2"><ToolLogo name={t} size={20} />{t}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PRICING.map((row) => (
                <tr key={row.tier} className="border-t border-border/60 align-top">
                  <td className="px-6 md:px-8 py-5 font-display tracking-[-0.02em] text-base md:text-lg">{row.tier}</td>
                  {row.cells.map((c, i) => (
                    <td key={i} className="px-4 md:px-5 py-5 text-sm md:text-base leading-snug">{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

const PROFILES = [
  {
    name: "Lovable",
    tag: "Full-stack AI app builder",
    body: [
      "Lovable lets you describe an app in plain language and get back a working full-stack app — React frontend, Postgres database, authentication, storage, edge functions and a public URL. You stay in a browser; there is no install.",
      "Lovable Cloud handles the backend so you don't pick a host, wire up Supabase yourself, or buy an OpenAI key — the AI gateway is built in. Most teams ship the first 80% of an MVP in Lovable in days, then continue prompting from there.",
    ],
    bests: ["Founders going from idea to MVP", "Internal tools and dashboards", "Marketing sites and landing pages", "Anyone who doesn't want to manage infra"],
    pricing: "Free, then Pro at $25/mo, Business at $50/mo, Enterprise on request.",
  },
  {
    name: "Cursor",
    tag: "AI-native code editor (fork of VS Code)",
    body: [
      "Cursor is a VS Code fork rebuilt around AI. You open a real codebase, and the editor pairs with you: Tab completions, an Agent that edits across files, Composer for multi-file refactors, and as of Cursor 3.5 (May 2026) Cloud Agents that run in isolated VMs and report back asynchronously.",
      "It's the tool serious vibe coders open every morning. You see every diff before it lands, which is exactly what you want when the codebase actually matters.",
    ],
    bests: ["Working in an existing repo", "Engineers who want full visibility", "Multi-file refactors", "Teams that already live in VS Code"],
    pricing: "Hobby free. Pro $20/mo. Pro+/Ultra extend usage. Teams $40/user/mo. Enterprise custom.",
  },
  {
    name: "Claude Code",
    tag: "Anthropic's agentic terminal coder",
    body: [
      "Claude Code runs in your terminal — and now also in VS Code, JetBrains, Slack and the web. You hand it a task and it reads the codebase, edits across files, runs tests and ships the change. It keeps long-term project context in a CLAUDE.md file, supports MCP tools, hooks, slash commands and custom skills.",
      "The current default is Claude Sonnet 4.5, with Opus available on the higher Max plans. It's the strongest pick when you want an agent to grind on a multi-step task without you babysitting it.",
    ],
    bests: ["Long, autonomous coding sessions", "Multi-file refactors and migrations", "Editor-agnostic teams", "Heavy CLI users"],
    pricing: "Claude Pro $20/mo. Max 5x $100/mo. Max 20x $200/mo. Team / Enterprise per seat. API pay-as-you-go.",
  },
  {
    name: "Codex",
    tag: "OpenAI's coding agent",
    body: [
      "Codex is OpenAI's agentic coding system. It runs in a CLI, an IDE extension (VS Code and JetBrains), on the web, on iOS, and inside the ChatGPT app — all using GPT-5.5, GPT-5.4 and the GPT-5.3-Codex specialised models. It can review pull requests on GitHub, kick off cloud tasks, and split work into subagents.",
      "If your team is already paying for ChatGPT Plus or Pro, Codex is effectively included — that's a big reason it has caught up to Cursor and Claude Code so quickly.",
    ],
    bests: ["Teams already on ChatGPT Plus/Pro", "GitHub-centric workflows", "Async cloud tasks", "Mobile / on-the-go coding"],
    pricing: "ChatGPT Plus $20/mo. Pro from $100/mo (5x or 20x). Business / Enterprise per seat. API key pay-as-you-go.",
  },
];

function Profiles() {
  return (
    <section aria-labelledby="pr-h" className="relative border-t border-border/60 bg-secondary/40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
        <h2 id="pr-h" className="fluid-h2 font-display max-w-[22ch]">A closer look at each.</h2>
        <div className="mt-14 space-y-6">
          {PROFILES.map((p) => (
            <article key={p.name} className="rounded-3xl border border-border bg-background p-8 md:p-12 grid md:grid-cols-12 gap-8 md:gap-10">
              <header className="md:col-span-4">
                <div className="flex items-center gap-3">
                  <ToolLogo name={p.name} size={40} />
                  <h3 className="font-display tracking-[-0.03em] leading-[1.02]" style={{ fontSize: "clamp(2rem, 3.4vw, 3rem)" }}>{p.name}</h3>
                </div>
                <p className="mt-3 text-base text-muted-foreground">{p.tag}</p>
              </header>
              <div className="md:col-span-8 space-y-5">
                {p.body.map((para, i) => (
                  <p key={i} className="text-base md:text-lg leading-relaxed text-foreground/90 max-w-[64ch]">{para}</p>
                ))}
                <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3 pt-6 border-t border-border/60">
                  <div>
                    <p className="text-sm uppercase tracking-wider text-muted-foreground mb-3">Best for</p>
                    <ul className="space-y-2">
                      {p.bests.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-sm md:text-base">
                          <Check className="size-4 mt-1 shrink-0 text-electric" strokeWidth={3} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-wider text-muted-foreground mb-3">Pricing</p>
                    <p className="text-sm md:text-base leading-snug text-foreground/90">{p.pricing}</p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const DECISIONS = [
  { q: "I'm starting from zero with a product idea.", a: "Lovable. It will get you to a working hosted app with a database faster than anything else in this list." },
  { q: "I already have a codebase I want to extend.", a: "Cursor for tight diff-by-diff control, or Claude Code / Codex if you want to hand off bigger tasks and review the result." },
  { q: "I want an agent that grinds on a refactor while I do something else.", a: "Claude Code (Sonnet 4.5) or Codex with GPT-5.5. Both run unattended in the cloud and report back." },
  { q: "I want to never touch a credit card or API key.", a: "Lovable. The Cloud backend and AI are bundled — no separate Supabase, no separate OpenAI account." },
  { q: "I want the deepest possible control of the codebase.", a: "Cursor. You stay inside an IDE, watch every change, and keep the project under your own version control." },
  { q: "I'm a solo founder who wants the cheapest serious setup.", a: "Lovable Pro ($25/mo) for the app + Cursor Pro ($20/mo) when you need an editor. Around $45/mo gets you a long way." },
];

const OTHERS = [
  {
    n: "Replit",
    b: "Browser IDE with an agent bolted on, plus instant hosting. Genuinely good if you want a container you can SSH into and a language that isn't JavaScript. Weaker than Lovable on design output — you get a working app that looks like a working app.",
    pick: "Scripts, backends, and anything that needs a real Linux box.",
  },
  {
    n: "Windsurf",
    b: "The other AI-native IDE. Same shape as Cursor, calmer interface, and its agent is more willing to run long without asking. If you bounced off Cursor's diff-approval rhythm, try it before you give up on IDE agents.",
    pick: "Cursor's job, for people who dislike Cursor's UX.",
  },
  {
    n: "v0",
    b: "Vercel's UI generator. Best-in-class at turning a screenshot or a sentence into clean React and Tailwind components. It is not an app builder — there's no real backend story — so treat it as a component factory that feeds your actual project.",
    pick: "Front-end components and design exploration, not whole products.",
  },
  {
    n: "Bolt",
    b: "Closest thing to a direct Lovable competitor: prompt in, full-stack app out, running in the browser. Fast to first render, thinner on the things that bite later — auth, database migrations, security defaults.",
    pick: "Throwaway prototypes and demos you won't have to maintain.",
  },
];

function RestOfField() {
  return (
    <section aria-labelledby="rof-h" className="relative border-t border-border/60 mx-auto max-w-[1100px] px-6 md:px-10 py-16 md:py-24">
      <h2 id="rof-h" className="fluid-h2 font-display max-w-[20ch]">The rest of the field.</h2>
      <p className="mt-8 max-w-[62ch] text-lg md:text-xl leading-relaxed text-foreground/80">
        Four tools cover most of the work, but these four come up in every comparison thread, and
        each one is genuinely the right answer for something. Here's where they actually fit.
      </p>
      <ul className="mt-12 grid md:grid-cols-2 gap-5">
        {OTHERS.map((o) => (
          <li key={o.n} className="rounded-3xl border border-border bg-background p-8">
            <h3 className="font-display tracking-[-0.03em]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2rem)" }}>
              {o.n}
            </h3>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-foreground/80">{o.b}</p>
            <p className="mt-6 pt-4 border-t border-border/60 text-sm text-foreground">
              <span className="text-muted-foreground">Pick it for: </span>
              {o.pick}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Decision() {
  return (
    <section aria-labelledby="d-h" className="relative border-t border-border/60 mx-auto max-w-[1100px] px-6 md:px-10 py-20 md:py-28">
      <h2 id="d-h" className="fluid-h2 font-display max-w-[22ch]">So which one should you pick?</h2>
      <dl className="mt-12 border-t border-border">
        {DECISIONS.map((d) => (
          <div key={d.q} className="grid md:grid-cols-12 gap-6 py-7 border-b border-border">
            <dt className="md:col-span-5 font-display text-lg md:text-2xl tracking-[-0.02em] leading-snug">{d.q}</dt>
            <dd className="md:col-span-6 md:col-start-7 text-base md:text-lg text-muted-foreground leading-relaxed">{d.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function FAQ() {
  return (
    <section aria-labelledby="faq-h" className="relative border-t border-border/60 mx-auto max-w-[1100px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="faq-h" className="fluid-h2 font-display max-w-[20ch]">Lovable vs the rest, answered.</h2>
      <dl className="mt-14 border-t border-border">
        {FAQS.map((f) => (
          <div key={f.q} className="grid md:grid-cols-12 gap-6 py-8 border-b border-border">
            <dt className="md:col-span-5 font-display text-xl md:text-2xl tracking-[-0.02em] leading-snug">{f.q}</dt>
            <dd className="md:col-span-6 md:col-start-7 text-base md:text-lg text-muted-foreground leading-relaxed">{f.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CTA() {
  return (
    <section aria-labelledby="cta-h" className="relative border-t border-border/60 bg-foreground text-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <h2 id="cta-h" className="font-display tracking-[-0.035em] leading-[1.02]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
            Use all four. We do.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-background/70 max-w-[54ch] leading-snug">
            We're a vibe coding agency. We ship in Lovable, refactor in Cursor, hand the hard stuff to Claude Code and Codex, and leave you with a project you can keep prompting on.
          </p>
        </div>
        <div className="md:col-span-4 md:col-start-9 space-y-4">
          <Link to="/services/lovable-development" className="block font-display text-electric-bright" style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.5rem)" }}>
            → Hire a Lovable expert
          </Link>
          <Link to="/services/vibe-coding-rescue" className="block font-display text-electric-bright" style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.5rem)" }}>
            → Rescue a stuck project
          </Link>
          <Link to="/guides/what-is-vibe-coding" className="block font-display text-electric-bright" style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.5rem)" }}>
            → What is vibe coding?
          </Link>
        </div>
      </div>
    </section>
  );
}
